import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Container,
  Grid,
  Button,
} from '@material-ui/core';
import { ArrowForward as ArrowForwardIcon } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { ROUTES } from '../../../../../app/routes/routeMap';
import InfoBlock from '../../../components/common/InfoBlock';
import IntlMessages from 'util/IntlMessages';

/**
 * Search Dropdown Integration Step — pointer-only step.
 *
 * As of the "UI Settings consolidation", the CSS-selector controls for the
 * classic search dropdown have moved out of the wizard and into the
 * operator-facing UI Settings surface. This step now only:
 *   - tells the user where the controls have moved,
 *   - keeps the wizard progression contract (onReady, onRegisterSave),
 *   - does NOT touch the legacy `inputSelectors` / `mobileInputSelectors` payload.
 *
 * Edge cases handled:
 *   1. Users who already saved selectors before the migration: the next
 *      publish keeps `inputSelectors` / `mobileInputSelectors` untouched
 *      because `updateViewIntegration` is no longer called here. Their
 *      existing values remain the source of truth until the user re-enters
 *      them through UI Settings.
 *   2. The wizard previously gated "Next" on the desktop-selectors array
 *      being non-empty. That gate is removed — the step is now always
 *      ready so users can move on. Empty selectors are surfaced as a
 *      warning inside UI Settings instead of blocking the wizard.
 *   3. The step still calls `onRegisterSave(noop)` so the wizard transport
 *      contract (StepComponent.onRegisterSave) stays intact for downstream
 *      consumers (Summary, publish flow, E2E test harness).
 *   4. `DropdownIntegrationStep` was historically also wired from
 *      `StepRouter` for `classic-integration` and `dropdown-integration`
 *      step IDs — both keep working without the editor surface.
 */
const DropdownIntegrationStep = ({
  onReady,
  onRegisterSave,
  title,
  shopId,
}) => {
  const currentShop = useSelector((s) => s.app.currentShop);
  const effectiveShopId = shopId || currentShop;

  useEffect(() => {
    if (onReady) onReady(true);
  }, [onReady]);

  const noopSave = useCallback(async () => {}, []);
  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(noopSave);
    }
  }, [onRegisterSave, noopSave]);

  const settingsHref = effectiveShopId
    ? ROUTES.searchSettings(effectiveShopId, 'searchbox')
    : null;

  return (
    <Container maxWidth="lg" style={{ height: '100%', paddingTop: 32, paddingBottom: 32 }}>
      <Grid container spacing={4} justify="center" style={{ height: '100%' }}>
        <Grid item xs={12} md={8} style={{ height: '100%' }}>
          <Paper
            elevation={1}
            data-testid="dropdown-integration-pointer"
            style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Box p={3} display="flex" flexDirection="column" height="100%">
              <Box mb={3}>
                <Typography variant="h4" component="h2">
                  {title || <IntlMessages id="search.settings.searchBox.dropdownIntegrationTitle" />}
                </Typography>
              </Box>
              <Divider />

              <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center" p={4}>
                <InfoBlock
                  title={<IntlMessages id="ui.integrationWizard.embedded.dropdownIntegrationStep.triggerSelectorsMoved" />}
                  description={<IntlMessages id="ui.integrationWizard.embedded.dropdownIntegrationStep.cssSelectorConfigurationForTheClassicSearchInputIsNowManaged" />}
                />

                {settingsHref ? (
                  <Box mt={3}>
                    <Button
                      component={RouterLink}
                      to={settingsHref}
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      data-testid="dropdown-integration-open-settings-btn"
                    >
                      <IntlMessages id="ui.integrationWizard.embedded.dropdownIntegrationStep.openUiSettings" />
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                    <IntlMessages id="ui.integrationWizard.embedded.dropdownIntegrationStep.selectAShopToOpenUiSettings" />
                  </Typography>
                )}

                <Typography variant="caption" color="textSecondary" style={{ marginTop: 24 }}>
                  <IntlMessages id="ui.integrationWizard.embedded.dropdownIntegrationStep.path" />
                  {' '}
                  <code>UI Settings → Search Dropdown → Integration → Trigger Selectors</code>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

DropdownIntegrationStep.propTypes = {
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  title: PropTypes.string,
  shopId: PropTypes.string,
};

DropdownIntegrationStep.defaultProps = {
  onReady: null,
  onRegisterSave: null,
  title: "ui.integrationWizard.embedded.dropdownIntegrationStep.searchDropdownIntegration",
  shopId: null,
};

export default DropdownIntegrationStep;
